/** Directory that contains files with code block contents */
export const CODE_DIR = '_code';

/** Directory that contains all of the content and determines routing */
export const CONTENT_DIR = 'content';

/** File that stores navigation metadata */
export const NAVIGATION_FILENAME = '_navigation.mdx';

/** Languages we may document in API Reference */
export const REFERENCE_LANGUAGES = [
  'curl',
  'json',
  'js',
  'ruby',
  'python',
  'go',
  'php',
  'java',
  'dotnet',
  'html',
  'hbs',
] as const;

/** Languages we store in local storage when you use the language select */
export const SAVED_LANGUAGES = [
  'curl',
  'js',
  'ruby',
  'python',
  'go',
  'php',
  'laravel',
  'java',
  'dotnet',
  'html',
] as const;

/**
 * Metadata that describes the languages that may be used in the code block:
 * - `key` is used to identify the language in the code or YAML data.
 * - `name` is a user-friendly label displayed in the language selector.
 * - `extensionGlob` is used to grab files that contain the code snippets associated with a particular language.
 *    Note how the actual file may have any extension, so that we can show bash commands or JSON snippets
 *    that are relevant just for a particular language.
 * Ordering of this array is important, as the first match will be used when determining the language for a file.
 */
export const LANGUAGE_META = [
  {
    key: 'fga',
    name: 'FGA Schema',
    extensionGlob: '.fga?(.*)',
  },
  {
    key: 'curl',
    name: 'cURL',
    extensionGlob: '.bash?(.*)',
  },
  {
    key: 'css',
    name: 'CSS',
    extensionGlob: '.css?(.*)',
  },
  {
    key: 'json',
    name: 'JSON',
    extensionGlob: '.json?(.*)',
  },
  {
    key: 'url',
    name: 'URL',
    extensionGlob: '.url?(.*)',
  },
  {
    key: 'js',
    name: 'JavaScript',
    extensionGlob: '.js?(.*)',
  },
  {
    key: 'jsx',
    name: 'JavaScript',
    extensionGlob: '.jsx?(.*)',
  },
  {
    key: 'html',
    name: 'HTML',
    extensionGlob: '.html?(.*)',
  },
  {
    key: 'ts',
    name: 'TypeScript',
    extensionGlob: '.ts?(.*)',
  },
  {
    key: 'ruby',
    name: 'Ruby',
    extensionGlob: '.rb?(.*)',
  },
  {
    key: 'python',
    name: 'Python',
    extensionGlob: '.py?(.*)',
  },
  {
    key: 'go',
    name: 'Go',
    extensionGlob: '.go?(.*)',
  },
  {
    key: 'php',
    name: 'PHP',
    extensionGlob: '.php?(.*)',
  },
  {
    key: 'laravel',
    name: 'Laravel',
    extensionGlob: '.laravel?(.*)',
  },
  {
    key: 'java',
    name: 'Java',
    extensionGlob: '.java?(.*)',
  },
  {
    key: 'dotnet',
    name: '.NET',
    extensionGlob: '.cs?(.*)',
  },
  {
    key: 'elixir',
    name: 'Elixir',
    extensionGlob: '.exs?(.*)',
  },
  {
    key: 'hbs',
    name: 'Handlebars',
    extensionGlob: '.hbs?(.*)',
  },
] as const;

export const SDK_META = [
  {
    key: 'node',
    name: 'Node.js',
    repo: 'workos-node',
    experimental: false,
  },
  {
    key: 'go',
    name: 'Go',
    repo: 'workos-go',
    experimental: false,
  },
  {
    key: 'ruby',
    name: 'Ruby',
    repo: 'workos-ruby',
    experimental: false,
  },
  {
    key: 'python',
    name: 'Python',
    repo: 'workos-python',
    experimental: false,
  },
  {
    key: 'php',
    name: 'PHP',
    repo: 'workos-php',
    experimental: false,
  },
  {
    key: 'laravel',
    name: 'Laravel',
    repo: 'workos-php-laravel',
    experimental: false,
  },
  {
    key: 'java',
    name: 'Java',
    repo: 'workos-kotlin',
    experimental: false,
  },
  {
    key: 'dotnet',
    name: '.NET',
    repo: 'workos-dotnet',
    experimental: false,
  },
  {
    key: 'elixir',
    name: 'Elixir',
    repo: 'workos-elixir',
    experimental: true,
  },
] as const;

export const AUTHKIT_SDK_META = [
  {
    key: 'authkit-js',
    name: 'JavaScript',
    repo: 'authkit-js',
  },
  {
    key: 'authkit-react',
    name: 'React',
    repo: 'authkit-react',
  },
  {
    key: 'authkit-nextjs',
    name: 'Next.js',
    repo: 'authkit-nextjs',
  },
  {
    key: 'authkit-remix',
    name: 'Remix',
    repo: 'authkit-remix',
  },
  {
    key: 'authkit-react-router',
    name: 'React Router',
    repo: 'authkit-react-router',
  },
  {
    key: 'authkit-tanstack-start',
    name: 'TanStack Start',
    repo: 'authkit-tanstack-start',
  },
] as const;
